import { motion } from 'framer-motion';
import type { ChatMessage as ChatMessageType } from '@vtak/shared';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { resolveEmblem } from '@/constants/emblems';
import { MarkdownContent } from './markdown-content';

interface ChatMessageProps {
  message: ChatMessageType;
}

/** Single chat message bubble with AI distinction and entrance animation */
export function ChatMessage({ message }: ChatMessageProps): React.JSX.Element {
  const isAi = message.source === 'ai';
  const time = formatTime(message.timestamp);
  const emblemUrl = useWorkspaceStore((s) => resolveEmblem(s.emblem));
  const showEmblem = message.source === 'web' && emblemUrl !== null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={cn(
        'rounded-md bg-white/[0.04] px-3 py-2 text-sm',
        isAi && 'border-l-[3px] border-l-ai bg-ai/[0.08]',
      )}
    >
      <div className="mb-0.5 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {showEmblem && emblemUrl && (
            <img
              src={emblemUrl}
              alt="unit"
              className="h-4 w-4 shrink-0 rounded-sm object-contain ring-1 ring-echo-border"
            />
          )}
          <span className={cn('text-xs font-semibold', isAi ? 'text-ai' : 'text-primary')}>
            {message.senderCallsign}
          </span>
          {message.source !== 'web' && (
            <Badge variant="secondary" className="h-4 px-1 text-[9px]">
              {message.source}
            </Badge>
          )}
        </div>
        <span className="text-[10px] text-muted-foreground">{time}</span>
      </div>
      {isAi ? (
        <MarkdownContent content={message.content} />
      ) : (
        <p className="text-foreground">{message.content}</p>
      )}
    </motion.div>
  );
}

/** Format ISO timestamp to HH:MM */
function formatTime(isoString: string): string {
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
